import dayjs from "dayjs";
import { CALL_COST_PER_MINUTE_USD } from "../../config/pricing";

export const generateChartData = (calls = []) => {
  const formatChartDateLabel = (dateValue) => dayjs(dateValue).format("MMM DD, YYYY");

  const resolveCallCost = (call = {}) => {
    const durationSeconds = Number(call?.duration || 0);
    if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return 0;
    return (durationSeconds / 60) * CALL_COST_PER_MINUTE_USD;
  };

  if (!calls.length) {
    return {
      callsOverTime: { labels: [], values: [] },
      callsByHour: { labels: [], values: [] },
      callsByHourOfDay: { labels: [], values: [] },
      statusDistribution: [],
      minutesTrend: { labels: [], values: [] },
      directionDistribution: [],
      avgDurationTrend: { labels: [], values: [] },
      queueTimeTrend: { labels: [], values: [] },
      costTrend: { labels: [], values: [] },
      avgCostPerCallTrend: { labels: [], values: [] },
      costByStatus: [],
      busyWeekdays: { labels: [], values: [] },
      peakHourWindows: { labels: [], values: [] },
      busiestDates: { labels: [], values: [] },
    };
  }

  const dateMap = {};
  const hourBucketMap = {};
  const hourOfDayMap = {};
  const statusMap = {};
  const minutesMap = {};
  const directionMap = {};
  const durationTotalsByDate = {};
  const queueTotalsByDate = {};
  const costTotalsByDate = {};
  const costByStatusMap = {};
  const callsByDate = {};
  const weekdayMap = {};

  calls.forEach((call) => {
    const date = dayjs(call.start_time).format("YYYY-MM-DD");
    const hour = dayjs(call.start_time).hour();
    const status = call.status || "unknown";
    const duration = call.duration || 0;
    const queueTime = call.queue_time || 0;
    const direction = call.direction || "unknown";
    const callCost = resolveCallCost(call);

    // Calls Over Time chart
    dateMap[date] = (dateMap[date] || 0) + 1;
    callsByDate[date] = (callsByDate[date] || 0) + 1;

    // Calls By Hour (3-hour buckets)
    const bucketStart = Math.floor(hour / 3) * 3;
    const bucketLabel = `${bucketStart}:00 - ${bucketStart + 3}:00`;

    hourBucketMap[bucketLabel] = (hourBucketMap[bucketLabel] || 0) + 1;
    const hourLabel = `${hour.toString().padStart(2, "0")}:00`;
    hourOfDayMap[hourLabel] = (hourOfDayMap[hourLabel] || 0) + 1;

    // Status Distribution chart
    statusMap[status] = (statusMap[status] || 0) + 1;

    // Minutes Trend chart
    minutesMap[date] = (minutesMap[date] || 0) + duration / 60;

    // Direction Distribution chart
    directionMap[direction] = (directionMap[direction] || 0) + 1;

    // Daily average duration trend (seconds)
    durationTotalsByDate[date] = (durationTotalsByDate[date] || 0) + duration;

    // Daily average queue trend (seconds)
    queueTotalsByDate[date] = (queueTotalsByDate[date] || 0) + queueTime;

    // Daily cost totals
    costTotalsByDate[date] = (costTotalsByDate[date] || 0) + callCost;
    costByStatusMap[status] = (costByStatusMap[status] || 0) + callCost;

    const weekday = dayjs(call.start_time).format("ddd");
    weekdayMap[weekday] = (weekdayMap[weekday] || 0) + 1;
  });

  const sortedDates = Object.keys(dateMap).sort(
    (a, b) => new Date(a) - new Date(b),
  );

  const predefinedBuckets = [
    "0:00 - 3:00",
    "3:00 - 6:00",
    "6:00 - 9:00",
    "9:00 - 12:00",
    "12:00 - 15:00",
    "15:00 - 18:00",
    "18:00 - 21:00",
    "21:00 - 24:00",
  ];
  const fullHourLabels = Array.from({ length: 24 }, (_, hour) =>
    `${hour.toString().padStart(2, "0")}:00`,
  );
  const orderedWeekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const peakWindows = [...predefinedBuckets]
    .map((bucket) => ({ label: bucket, value: hourBucketMap[bucket] || 0 }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);
  const busiestDates = [...sortedDates]
    .map((date) => ({ label: formatChartDateLabel(date), value: callsByDate[date] || 0 }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 7);

  return {
    // Calls Over Time data
    callsOverTime: {
      labels: sortedDates.map((date) => formatChartDateLabel(date)),
      values: sortedDates.map((date) => dateMap[date]),
    },

    // Calls By Hour data
    callsByHour: {
      labels: predefinedBuckets,
      values: predefinedBuckets.map((bucket) => hourBucketMap[bucket] || 0),
    },

    callsByHourOfDay: {
      labels: fullHourLabels,
      values: fullHourLabels.map((hourLabel) => hourOfDayMap[hourLabel] || 0),
    },

    // Status Distribution data
    statusDistribution: Object.entries(statusMap).map(([key, value]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value,
    })),

    // Minutes Usage Trend data
    minutesTrend: {
      labels: sortedDates.map((date) => formatChartDateLabel(date)),
      values: sortedDates.map((date) => +minutesMap[date]?.toFixed(1)),
    },

    // Direction Distribution data
    directionDistribution: Object.entries(directionMap).map(([key, value]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value,
    })),

    // Average call duration per day (seconds)
    avgDurationTrend: {
      labels: sortedDates.map((date) => formatChartDateLabel(date)),
      values: sortedDates.map((date) =>
        +(
          (durationTotalsByDate[date] || 0) / Math.max(callsByDate[date] || 1, 1)
        ).toFixed(1),
      ),
    },

    // Average queue time per day (seconds)
    queueTimeTrend: {
      labels: sortedDates.map((date) => formatChartDateLabel(date)),
      values: sortedDates.map((date) =>
        +(
          (queueTotalsByDate[date] || 0) / Math.max(callsByDate[date] || 1, 1)
        ).toFixed(1),
      ),
    },

    costTrend: {
      labels: sortedDates.map((date) => formatChartDateLabel(date)),
      values: sortedDates.map((date) => +(costTotalsByDate[date] || 0).toFixed(2)),
    },

    avgCostPerCallTrend: {
      labels: sortedDates.map((date) => formatChartDateLabel(date)),
      values: sortedDates.map((date) =>
        +(
          (costTotalsByDate[date] || 0) / Math.max(callsByDate[date] || 1, 1)
        ).toFixed(4),
      ),
    },

    costByStatus: Object.entries(costByStatusMap).map(([key, value]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value: +value.toFixed(2),
    })),

    busyWeekdays: {
      labels: orderedWeekdays,
      values: orderedWeekdays.map((day) => weekdayMap[day] || 0),
    },

    peakHourWindows: {
      labels: peakWindows.map((item) => item.label),
      values: peakWindows.map((item) => item.value),
    },

    busiestDates: {
      labels: busiestDates.map((item) => item.label),
      values: busiestDates.map((item) => item.value),
    },
  };
};
